import { Img, SizeValue, Space, Typography } from '@ag1/ui';
import { ORDER_ITEM_TYPE } from '@portal/schema/subscription/orderItemType';
import { useMedia } from '@portal/state/context/MediaContext';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import { useV53AccountIdentifier } from '@portal/state/hooks/useV53AccountIdentifier';
import { OrderLineItemV2 } from '@portal/state/internal/getOrderDetails/types';
import { SubscriptionLineItemV2 } from '@portal/state/internal/getSubscriptionDetails/types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useMoney } from 'shared/hooks/useMoney';
import { useTitleForInStockProduct } from 'shared/hooks/useTitleForInstockProduct';
import { createPureSelectors, getStylesFromSelectors } from 'shared/utils/classNameSelectors';
import {
  isAppliedAfterNextOrderOnlyOrderItem,
  isNextOrderOnlyOrderItem,
  isSubscriptionOrderItem
} from 'shared/utils/getOrderItemType';
import { ProductLineItem } from '../ProductLineItem/ProductLineItem';
import styles from './OrderBodyNoEditableV2.module.scss';

type LineItemV2 = SubscriptionLineItemV2 | OrderLineItemV2;

interface OrderBodyNoEditableV2Props {
  item: LineItemV2;
  type?: ORDER_ITEM_TYPE;
  currencyCode?: string;
  className?: string;
  size?: SizeValue | ResponsiveSizeValue<SizeValue>;
  quantityLabel: string;
  outOfStockLabel: string;
  label?: string;
  inStockStatus?: boolean;
  hideSubtitle?: boolean;
  boldPriceLabel?: boolean;
}

export const OrderBodyNoEditableV2 = ({
  item,
  type,
  currencyCode,
  className,
  size = 'small',
  quantityLabel,
  outOfStockLabel,
  label,
  inStockStatus = true,
  hideSubtitle = false,
  boldPriceLabel = true
}: OrderBodyNoEditableV2Props) => {
  const isV53Account = useV53AccountIdentifier();
  const { t } = useTranslation('subscriptionDetails');
  const { formatMoney } = useMoney();
  const { currency } = useStoreCurrency();

  const variantId = item.product.shopify_variant_id;
  const isSubscriptionItem = isSubscriptionOrderItem(type);
  const isNextOrderOnlyItem = isNextOrderOnlyOrderItem(type);
  const isAppliedAfterNextOrderOnlyItem = isAppliedAfterNextOrderOnlyOrderItem(type);

  const classSizes = getStylesFromSelectors(createPureSelectors(size, 'small'), styles);
  const stockTitle = useTitleForInStockProduct(variantId);

  const { atSmall } = useMedia();

  const originalPrice = item.total_amount + item.discount_amount;

  const currencyCodeToUse = currencyCode || currency;
  const originalPriceFormatted = formatMoney(originalPrice, currencyCodeToUse);

  if (isV53Account) {
    return (
      <ProductLineItem item={item} hideSubtitle={hideSubtitle} currencyCode={currencyCodeToUse} />
    );
  }

  return (
    <div
      className={clsx(className, classSizes, styles.itemContainer)}
      data-testid={isSubscriptionItem ? 'subscription-item' : 'order-item'}>
      <div className={styles.wrapper}>
        <figure className={styles.pic}>
          <Img
            src={item.product.image_url}
            aspectRatio={label && !atSmall ? 1.5 : 1.3333}
            className={clsx({
              [styles.placeholder]: inStockStatus,
              [styles.inStock]: !inStockStatus,
              [styles.placeholderLabel]: label
            })}
          />

          <div
            className={clsx(
              styles.imgDescription,
              isSubscriptionItem && styles.subscription,
              isNextOrderOnlyItem && styles.nextOrder,
              isAppliedAfterNextOrderOnlyItem && styles.appliedAfterNextOrder
            )}>
            {isSubscriptionItem && t('v2.orderItems.subscriptionDescription')}
            {isNextOrderOnlyItem && t('v2.orderItems.nextOrderOnlyDescription')}
            {isAppliedAfterNextOrderOnlyItem && t('v2.orderItems.toBeAppliedAfterNextOrder')}
          </div>
        </figure>
        <div className={clsx([styles.data, { [styles.dataWithLabel]: label }])}>
          <div className={styles.priceDataWrap}>
            <Typography
              variant="portalText"
              size="2"
              weight="700"
              component="p"
              color="black-new"
              noPadding>
              {item.product.name || stockTitle}
            </Typography>
            {inStockStatus && (
              <Typography
                variant="portalText"
                size="2"
                weight={boldPriceLabel ? '700' : '400'}
                color={originalPrice === 0 ? 'brown-new' : 'black-new'}
                noPadding
                data-testid="product-price"
                className={styles.price}>
                {originalPriceFormatted}
              </Typography>
            )}
          </div>

          <div>
            <Typography variant="portalCaption" size="1" color="grey-1-new" noPadding>
              {quantityLabel} {item.quantity}
            </Typography>
            {!inStockStatus && (
              <Space top={{ xxs: 4, s: 8 }}>
                <Typography
                  variant="portalText"
                  size="2"
                  weight="700"
                  color="grey-3-new"
                  className={styles.price}
                  noPadding>
                  {outOfStockLabel}
                </Typography>
              </Space>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
